import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2,
  Users,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = ['Fixo', 'Quinzenal', 'Mensal', 'Eventual', 'Avaliando'];
const ROLES = ['VOLUNTEER', 'ADMIN', 'SUPER_ADMIN'];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFrequency, setFilterFrequency] = useState('all');
  const [filterCongregation, setFilterCongregation] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    frequency: 'Eventual',
    dc50: false,
    training: false,
    builder: '',
    congregation: '',
    phone: '',
    role: 'VOLUNTEER',
    function_ids: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, functionsRes] = await Promise.all([
        fetch(`${API_URL}/api/users`, { credentials: 'include' }),
        fetch(`${API_URL}/api/functions`, { credentials: 'include' }),
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (functionsRes.ok) setFunctions(await functionsRes.json());
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }

    try {
      const url = editingUser 
        ? `${API_URL}/api/users/${editingUser.user_id}`
        : `${API_URL}/api/users`;
      
      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingUser ? 'Usuário atualizado' : 'Usuário criado');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar usuário');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Erro ao salvar usuário');
    }
  };

  const handleDelete = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Usuário excluído');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir usuário');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erro ao excluir usuário');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const confirmDelete = (userId) => {
    setDeleteTarget(userId);
    setDeleteConfirmOpen(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      frequency: user.frequency,
      dc50: user.dc50,
      training: user.training,
      builder: user.builder || '',
      congregation: user.congregation || '',
      phone: user.phone || '',
      role: user.role,
      function_ids: user.function_ids || [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      frequency: 'Eventual',
      dc50: false,
      training: false,
      builder: '',
      congregation: '',
      phone: '',
      role: 'VOLUNTEER',
      function_ids: [],
    });
  };

  const getFunctionName = (functionId) => {
    const func = functions.find(f => f.function_id === functionId);
    return func?.name || functionId;
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      'Fixo': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      'Quinzenal': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'Mensal': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'Eventual': 'bg-zinc-700 text-zinc-300 border-zinc-600',
      'Avaliando': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    };
    return colors[frequency] || 'bg-zinc-700 text-zinc-300';
  };

  const getRoleBadge = (role) => {
    const colors = {
      'SUPER_ADMIN': 'bg-red-500/10 text-red-400 border-red-500/20',
      'ADMIN': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      'VOLUNTEER': 'bg-zinc-700 text-zinc-400 border-zinc-600',
    };
    return colors[role] || 'bg-zinc-700 text-zinc-400';
  };

  // Get unique congregations for filter
  const congregations = [...new Set(users.map(u => u.congregation).filter(Boolean))];

  // Filter users
  const filteredUsers = users.filter(user => {
    if (searchTerm && !user.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterFrequency !== 'all' && user.frequency !== filterFrequency) return false;
    if (filterCongregation !== 'all' && user.congregation !== filterCongregation) return false;
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-zinc-50 tracking-tight flex items-center gap-3">
              <Users className="w-8 h-8" />
              Voluntários
            </h1>
            <p className="text-zinc-400 mt-1">Gerencie os voluntários do sistema</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="w-full bg-white text-black hover:bg-zinc-200 sm:w-auto" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Voluntário
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-zinc-100">
                  {editingUser ? 'Editar Voluntário' : 'Novo Voluntário'}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-4 pt-4 sm:grid-cols-2">
                <div className="col-span-2 space-y-2">
                  <Label className="text-zinc-400">Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nome completo"
                    className="bg-zinc-950 border-zinc-800"
                    data-testid="user-name-input"
                  />
                </div>

                <div className="col-span-2 space-y-2">
                  <Label className="text-zinc-400">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@exemplo.com"
                    className="bg-zinc-950 border-zinc-800"
                    disabled={!!editingUser}
                    data-testid="user-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Frequência</Label>
                  <Select 
                    value={formData.frequency} 
                    onValueChange={(v) => setFormData({...formData, frequency: v})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {FREQUENCIES.map(freq => (
                        <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Perfil</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(v) => setFormData({...formData, role: v})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {ROLES.map(role => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Congregação</Label>
                  <Input
                    value={formData.congregation}
                    onChange={(e) => setFormData({...formData, congregation: e.target.value})}
                    placeholder="Nome da congregação"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Celular</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(00) 00000-0000"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Builder</Label>
                  <Input
                    value={formData.builder}
                    onChange={(e) => setFormData({...formData, builder: e.target.value})}
                    placeholder="B1, B2, etc."
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>

                <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:gap-8 sm:pt-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.dc50}
                      onCheckedChange={(v) => setFormData({...formData, dc50: v})}
                    />
                    <Label className="text-zinc-400">DC-50</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.training}
                      onCheckedChange={(v) => setFormData({...formData, training: v})}
                    />
                    <Label className="text-zinc-400">Treinamento</Label>
                  </div>
                </div>

                <div className="col-span-2 pt-4">
                  <Button 
                    onClick={handleSubmit}
                    className="w-full bg-white text-black hover:bg-zinc-200"
                    data-testid="save-user-btn"
                  >
                    {editingUser ? 'Salvar Alterações' : 'Criar Voluntário'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950 border-zinc-800"
                  data-testid="search-users"
                />
              </div>
              <Select value={filterFrequency} onValueChange={setFilterFrequency}>
                <SelectTrigger className="w-full bg-zinc-950 border-zinc-800 md:w-[160px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Frequência" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Todas</SelectItem>
                  {FREQUENCIES.map(freq => (
                    <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {congregations.length > 0 && (
                <Select value={filterCongregation} onValueChange={setFilterCongregation}>
                  <SelectTrigger className="w-full bg-zinc-950 border-zinc-800 md:w-[180px]">
                    <SelectValue placeholder="Congregação" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all">Todas</SelectItem>
                    {congregations.map(cong => (
                      <SelectItem key={cong} value={cong}>{cong}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-0">
            <div className="md:hidden divide-y divide-zinc-800">
              {filteredUsers.length === 0 ? (
                <div className="py-8 text-center text-zinc-500">
                  Nenhum voluntário encontrado
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div key={`mobile-${user.user_id}`} className="p-4 space-y-4" data-testid={`user-card-${user.user_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-200 break-words">{user.name}</p>
                        <p className="text-xs text-zinc-500 break-all">{user.email}</p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(user)}
                          className="h-8 w-8 text-zinc-400 hover:text-zinc-200"
                          data-testid={`edit-user-${user.user_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => confirmDelete(user.user_id)}
                          className="h-8 w-8 text-zinc-400 hover:text-red-400"
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge className={`${getFrequencyBadge(user.frequency)} border`}>
                        {user.frequency}
                      </Badge>
                      <Badge className={`${getRoleBadge(user.role)} border`}>
                        {user.role}
                      </Badge>
                      {user.dc50 && (
                        <Badge className="bg-green-500/10 text-green-400 border border-green-500/20 text-xs">
                          DC-50
                        </Badge>
                      )}
                      {user.training && (
                        <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs">
                          Treinado
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-zinc-500">Congregação</p>
                        <p className="text-zinc-300">{user.congregation || '-'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Builder</p>
                        <p className="text-zinc-300">{user.builder || '-'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500">Celular</p>
                        <p className="text-zinc-300">{user.phone || '-'}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Nome</TableHead>
                    <TableHead className="text-zinc-400">Frequência</TableHead>
                    <TableHead className="text-zinc-400">Congregação</TableHead>
                    <TableHead className="text-zinc-400">Perfil</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                        Nenhum usuário encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user.user_id} className="border-zinc-800" data-testid={`user-row-${user.user_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-zinc-200">{user.name}</p>
                            <p className="text-xs text-zinc-500">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getFrequencyBadge(user.frequency)} border`}>
                            {user.frequency}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {user.congregation || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getRoleBadge(user.role)} border`}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {user.dc50 && (
                              <Badge className="bg-green-500/10 text-green-400 border border-green-500/20 text-xs">
                                DC-50
                              </Badge>
                            )}
                            {user.training && (
                              <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-xs">
                                Treinado
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(user)}
                              className="h-8 w-8 text-zinc-400 hover:text-zinc-200"
                              data-testid={`edit-user-${user.user_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => confirmDelete(user.user_id)}
                              className="h-8 w-8 text-zinc-400 hover:text-red-400"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="text-sm text-zinc-500">
          Mostrando {filteredUsers.length} de {users.length} voluntários
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-zinc-100">Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Tem certeza que deseja excluir este voluntário? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => deleteTarget && handleDelete(deleteTarget)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
